import { Flex, SkeletonBox, Spacing } from '@billy/design-system'
import { ReactElement, useCallback } from 'react'
import { useLocalStorage } from 'react-use'

import { InvoiceDocumentType } from '@features/invoices/enums/invoiceDocumentType'
import { isDocumentTypeQuote } from '@features/invoices/utils/isDocumentTypeQuote'
import { useGetZervantDocumentSettings } from '@features/invoicingSettings/hooks/useGetZervantDocumentSettings'

import { LocalStorageKeys } from '../../../../../../../../../../enums/localStorageKeys'
import { useInvoiceEditorForm } from '../../../../../../contexts/invoiceEditorFormContext'
import { useInvoiceEditorDocumentType } from '../../../../../../hooks/useInvoiceEditorDocumentType'
import { InvoicePrefixAndNumberContextProvider } from './contexts/invoicePrefixAndNumberContext'
import { QuoteNumberContextProvider } from './contexts/quoteNumberContext'
import { DepositInvoiceCurrencyChangeBanner } from './elements/DepositInvoiceCurrencyChangeBanner'
import { InvoiceConfiguration } from './elements/InvoiceConfiguration'
import { InvoiceLanguageChangePreviewBanner } from './elements/InvoiceLanguageChangePreviewBanner'
import { InvoicePrefixAndNumber } from './elements/InvoicePrefixAndNumber'
import { QuoteNumber } from './elements/QuoteNumber'

export const InvoiceHeader = (): ReactElement => {
  const [isLanguageBannerClosed, setIsLanguageBannerClosed] = useLocalStorage<boolean | undefined>(
    LocalStorageKeys.HideInvoiceLanguageChangePreviewBanner,
  )
  const [isCurrencyBannerClosed, setIsCurrencyBannerClosed] = useLocalStorage<boolean | undefined>(
    LocalStorageKeys.HideDepositInvoiceCurrencyChangeBanner,
  )
  const { isLoading: isFormLoading } = useInvoiceEditorForm()
  const { isLoading: isSettingsLoading } = useGetZervantDocumentSettings(InvoiceDocumentType.Invoice)
  const { documentType } = useInvoiceEditorDocumentType()

  const shouldDisplayLocalSettings = documentType !== InvoiceDocumentType.CreditNote
  const isQuote = isDocumentTypeQuote(documentType)
  const shouldHideLanguageBanner = isLanguageBannerClosed === undefined || isLanguageBannerClosed

  const handleLanguageChange = useCallback(() => {
    if (isLanguageBannerClosed === undefined) {
      setIsLanguageBannerClosed(false)
    }
  }, [isLanguageBannerClosed, setIsLanguageBannerClosed])

  const handleLanguageBannerClose = useCallback(() => {
    setIsLanguageBannerClosed(true)
  }, [setIsLanguageBannerClosed])

  const handleCurrencyBannerClose = useCallback(() => {
    setIsCurrencyBannerClosed(true)
  }, [setIsCurrencyBannerClosed])

  if (isFormLoading || isSettingsLoading) {
    return <SkeletonBox fullWidth />
  }

  return (
    <>
      {isQuote && !isCurrencyBannerClosed && <DepositInvoiceCurrencyChangeBanner onClose={handleCurrencyBannerClose} />}
      {!shouldHideLanguageBanner && <InvoiceLanguageChangePreviewBanner onClose={handleLanguageBannerClose} />}
      {isQuote ? (
        <QuoteNumberContextProvider>
          <Flex alignItems="center" gap={Spacing.S8}>
            <QuoteNumber />
            <InvoiceConfiguration onLanguageChange={handleLanguageChange} />
          </Flex>
        </QuoteNumberContextProvider>
      ) : (
        <InvoicePrefixAndNumberContextProvider>
          <Flex alignItems="center" gap={Spacing.S8}>
            <InvoicePrefixAndNumber />
            {shouldDisplayLocalSettings && <InvoiceConfiguration onLanguageChange={handleLanguageChange} />}
          </Flex>
        </InvoicePrefixAndNumberContextProvider>
      )}
    </>
  )
}
